import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, X, XCircle, CreditCard } from 'lucide-react';
const plans = [{
  name: 'Базовый',
  price: '$40',
  period: '/ мес.',
  isCurrent: false,
  features: ['До 50 диалогов в день', 'Базовые критерии подбора', 'Поддержка по email']
}, {
  name: 'Стандарт',
  price: '$100',
  period: '/ 3 мес.',
  isCurrent: true,
  features: ['До 300 диалогов в день', 'Расширенные критерии подбора', 'Анализ фото и психотесты', 'Приоритетная поддержка']
}, {
  name: 'Премиум',
  price: '$300',
  period: '/ год',
  isCurrent: false,
  features: ['Безлимитные диалоги', 'Все критерии и фильтры', 'Анализ фото и психотесты', 'Персональный менеджер', 'Ранний доступ к новым функциям']
}];
const paymentHistory = [{
  id: '1',
  date: '15 декабря 2025',
  amount: '$100',
  status: 'Оплачено'
}, {
  id: '2',
  date: '15 сентября 2025',
  amount: '$100',
  status: 'Оплачено'
}, {
  id: '3',
  date: '15 июня 2025',
  amount: '$100',
  status: 'Оплачено'
}];
type ModalState = 'purchase-confirm' | 'loading' | 'success' | 'cancel-confirm' | 'cancel-success' | null;
export function SubscriptionScreen() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [modalState, setModalState] = useState<ModalState>(null);
  const handleSelectPlan = (planName: string) => {
    setSelectedPlan(planName);
    setModalState('purchase-confirm');
  };
  const handleConfirmPurchase = () => {
    setModalState('loading');
    setTimeout(() => {
      setModalState('success');
    }, 2200);
  };
  const handleCancelClick = () => {
    setModalState('cancel-confirm');
  };
  const handleConfirmCancel = () => {
    setModalState('cancel-success');
  };
  const closeModal = () => {
    setSelectedPlan(null);
    setModalState(null);
  };
  return <div className="min-h-screen bg-svt-bg" data-id="element-339">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-svt-bg/80 backdrop-blur-xl border-b border-svt-border transition-colors duration-300" data-id="element-340">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-341">
          <h1 className="text-2xl font-bold text-svt-text" data-id="element-342">Подписка</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6" data-id="element-343">
        {/* Current Plan Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="relative rounded-3xl overflow-hidden bg-svt-card border border-svt-border p-6" data-id="element-344">
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-primary opacity-10 blur-3xl rounded-full" data-id="element-345"></div>

          <div className="relative" data-id="element-346">
            <div className="flex items-start justify-between mb-4" data-id="element-347">
              <div data-id="element-348">
                <h2 className="text-xl font-bold text-svt-text mb-1" data-id="element-349">
                  Текущий план
                </h2>
                <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-350">
                  Стандарт
                </p>
              </div>
              <div className="px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-xs font-bold" data-id="element-351">
                Активна
              </div>
            </div>

            <div className="flex items-baseline gap-2 mb-2" data-id="element-352">
              <span className="text-3xl font-bold text-svt-text" data-id="element-353">$100</span>
              <span className="text-svt-text-muted" data-id="element-354">/ 3 месяца</span>
            </div>

            <p className="text-sm text-svt-text-muted mb-5" data-id="element-355">
              Действует до{' '}
              <span className="text-svt-text font-medium" data-id="element-356">15 марта 2026</span>
            </p>

            <div className="pt-5 border-t border-svt-border" data-id="element-357">
              <h4 className="text-xs font-bold tracking-widest uppercase text-svt-text-muted mb-3" data-id="element-358">
                Включено в тариф
              </h4>
              <ul className="space-y-2.5" data-id="element-359">
                {plans.find(p => p.isCurrent)?.features.map((feature, idx) => <li key={idx} className="flex items-start gap-2.5" data-id="element-360">
                      <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gradient-primary/20 flex items-center justify-center mt-0.5" data-id="element-361">
                        <Check size={10} className="text-svt-pink" strokeWidth={3} data-id="element-362" />
                      </div>
                      <span className="text-sm text-svt-text-secondary leading-snug" data-id="element-363">
                        {feature}
                      </span>
                    </li>)}
              </ul>
            </div>
            <button onClick={handleCancelClick} className="mt-5 w-full py-2.5 rounded-full border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/10 hover:border-red-500/50 transition-all" data-id="element-364">
              Отменить подписку
            </button>
          </div>
        </motion.div>

        {/* Plan Options */}
        <div data-id="element-365">
          <h3 className="text-xs font-bold tracking-widest uppercase text-svt-text-muted mb-4" data-id="element-366">
            Доступные тарифы
          </h3>
          <div className="grid grid-cols-1 gap-4" data-id="element-367">
            {plans.map((plan, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1 + index * 0.05
          }} className={`relative p-6 rounded-2xl flex flex-col transition-all duration-300 ${plan.isCurrent ? 'bg-svt-card-highlight border border-svt-pink/40 shadow-[0_0_20px_rgba(232,67,147,0.1)]' : 'bg-svt-card-alt border border-svt-border hover:border-svt-border-hover'}`} data-id="element-368">
                <div className="flex items-center justify-between mb-4" data-id="element-369">
                  <h4 className="text-lg font-bold text-svt-text" data-id="element-370">
                    {plan.name}
                  </h4>
                  {plan.isCurrent && <div className="w-6 h-6 rounded-full bg-gradient-primary flex items-center justify-center" data-id="element-371">
                      <Check size={14} className="text-white" data-id="element-372" />
                    </div>}
                </div>

                <div className="flex items-baseline gap-1 mb-5" data-id="element-373">
                  <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-374">
                    {plan.price}
                  </span>
                  <span className="text-svt-text-muted" data-id="element-375">{plan.period}</span>
                </div>

                <ul className="space-y-2.5 mb-6" data-id="element-376">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-start gap-2.5" data-id="element-377">
                      <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gradient-primary/20 flex items-center justify-center mt-0.5" data-id="element-378">
                        <Check size={10} className="text-svt-pink" strokeWidth={3} data-id="element-379" />
                      </div>
                      <span className="text-sm text-svt-text-secondary leading-snug" data-id="element-380">
                        {feature}
                      </span>
                    </li>)}
                </ul>

                <button disabled={plan.isCurrent} onClick={() => handleSelectPlan(plan.name)} className={`w-full py-3 rounded-full font-medium transition-all duration-300 mt-auto ${plan.isCurrent ? 'bg-svt-surface text-svt-text-muted cursor-not-allowed' : 'border border-svt-pink/50 text-svt-pink-light hover:bg-svt-pink/10 hover:border-svt-pink'}`} data-id="element-381">
                  {plan.isCurrent ? 'Текущий' : 'Выбрать'}
                </button>
              </motion.div>)}
          </div>
        </div>

        {/* Payment History */}
        <div data-id="element-382">
          <h3 className="text-xs font-bold tracking-widest uppercase text-svt-text-muted mb-4" data-id="element-383">
            История платежей
          </h3>
          <div className="space-y-3" data-id="element-384">
            {paymentHistory.map((payment, index) => <motion.div key={payment.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.3,
            delay: 0.3 + index * 0.05
          }} className="p-4 rounded-2xl bg-svt-surface border border-svt-border flex items-center justify-between" data-id="element-385">
                <div data-id="element-386">
                  <p className="text-sm font-medium text-svt-text mb-1" data-id="element-387">
                    {payment.date}
                  </p>
                  <p className="text-xs text-svt-text-muted" data-id="element-388">
                    {payment.status}
                  </p>
                </div>
                <div className="text-lg font-bold text-svt-text" data-id="element-389">
                  {payment.amount}
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      <AnimatePresence data-id="element-390">
        {modalState && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm" onClick={modalState === 'success' || modalState === 'cancel-success' || modalState === 'purchase-confirm' ? closeModal : undefined} data-id="element-391">
            <motion.div key={modalState} initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-sm rounded-3xl bg-svt-card border border-svt-border p-8 shadow-2xl overflow-hidden" data-id="element-392">
              <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 opacity-20 blur-3xl rounded-full pointer-events-none ${modalState === 'success' ? 'bg-green-500' : 'bg-gradient-primary'}`} data-id="element-393"></div>

              {modalState === 'purchase-confirm' && (() => {
            const plan = plans.find(p => p.name === selectedPlan);
            return <div className="relative flex flex-col items-center text-center" data-id="element-394">
                      <button onClick={closeModal} className="absolute top-0 right-0 w-8 h-8 rounded-full bg-svt-surface border border-svt-border hover:bg-svt-surface-hover transition-colors flex items-center justify-center z-10" data-id="element-395">
                        <X size={16} className="text-svt-text-muted" data-id="element-396" />
                      </button>
                      <div className="relative mb-6" data-id="element-397">
                        <div className="absolute inset-0 bg-gradient-primary opacity-30 blur-2xl rounded-full" data-id="element-398"></div>
                        <div className="relative w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center shadow-[0_0_30px_rgba(232,67,147,0.4)]" data-id="element-399">
                          <CreditCard size={36} className="text-white" data-id="element-400" />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-svt-text mb-3" data-id="element-401">
                        Подтвердите покупку
                      </h3>

                      <div className="w-full p-4 rounded-2xl bg-svt-surface border border-svt-border mb-5" data-id="element-402">
                        <div className="flex items-center justify-between mb-3 pb-3 border-b border-svt-border" data-id="element-403">
                          <span className="text-sm text-svt-text-muted" data-id="element-404">
                            Тариф
                          </span>
                          <span className="text-sm font-semibold text-svt-text" data-id="element-405">
                            {selectedPlan}
                          </span>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-406">
                          <span className="text-sm text-svt-text-muted" data-id="element-407">
                            Стоимость
                          </span>
                          <div className="text-right" data-id="element-408">
                            <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-409">
                              {plan?.price}
                            </span>
                            <span className="text-sm text-svt-text-muted ml-1" data-id="element-410">
                              {plan?.period}
                            </span>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-svt-text-muted leading-relaxed mb-8" data-id="element-411">
                        Оплата подписки будет произведена через{' '}
                        <span className="font-semibold text-svt-text" data-id="element-412">
                          Tribute
                        </span>
                        . Вы будете перенаправлены для завершения платежа.
                      </p>

                      <div className="flex gap-3 w-full" data-id="element-413">
                        <button onClick={closeModal} className="flex-1 py-3 rounded-full border border-svt-border text-svt-text font-semibold text-sm hover:bg-svt-surface-hover transition-all" data-id="element-414">
                          Отмена
                        </button>
                        <button onClick={handleConfirmPurchase} className="flex-1 py-3 rounded-full bg-gradient-primary text-white font-semibold text-sm hover:opacity-90 transition-all shadow-[0_0_20px_rgba(232,67,147,0.3)]" data-id="element-415">
                          Купить
                        </button>
                      </div>
                    </div>;
          })()}

              {modalState === 'loading' && <>
                  <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-svt-surface border border-svt-border hover:bg-svt-surface-hover transition-colors flex items-center justify-center z-10" data-id="element-416">
                    <X size={16} className="text-svt-text-muted" data-id="element-417" />
                  </button>

                  <div className="relative flex flex-col items-center text-center" data-id="element-418">
                    <div className="relative mb-6" data-id="element-419">
                      <div className="absolute inset-0 bg-gradient-primary opacity-30 blur-2xl rounded-full" data-id="element-420"></div>
                      <div className="relative w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center shadow-[0_0_30px_rgba(232,67,147,0.4)]" data-id="element-421">
                        <Loader2 size={36} className="text-white animate-spin" data-id="element-422" />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-svt-text mb-3" data-id="element-423">
                      Оформление подписки
                    </h3>

                    <p className="text-sm text-svt-text-secondary leading-relaxed mb-2" data-id="element-424">
                      Тариф{' '}
                      <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-425">
                        {selectedPlan}
                      </span>
                    </p>

                    <p className="text-sm text-svt-text-muted leading-relaxed mb-6" data-id="element-426">
                      Подписка оформляется через{' '}
                      <span className="font-semibold text-svt-text" data-id="element-427">
                        Tribute
                      </span>
                      . Пожалуйста, подождите...
                    </p>

                    <div className="w-full h-1 bg-svt-surface rounded-full overflow-hidden" data-id="element-428">
                      <motion.div className="h-full bg-gradient-primary rounded-full" initial={{
                  x: '-100%'
                }} animate={{
                  x: '100%'
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'linear'
                }} style={{
                  width: '50%'
                }} data-id="element-429" />
                    </div>
                  </div>
                </>}

              {modalState === 'success' && <div className="relative flex flex-col items-center text-center" data-id="element-430">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              duration: 0.4,
              delay: 0.1,
              type: 'spring',
              stiffness: 200
            }} className="relative mb-6" data-id="element-431">
                    <div className="absolute inset-0 bg-green-500 opacity-30 blur-2xl rounded-full" data-id="element-432"></div>
                    <div className="relative w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-[0_0_30px_rgba(34,197,94,0.4)]" data-id="element-433">
                      <Check size={40} className="text-white" strokeWidth={3} data-id="element-434" />
                    </div>
                  </motion.div>

                  <h3 className="text-xl font-bold text-svt-text mb-3" data-id="element-435">
                    Подписка оформлена!
                  </h3>

                  <p className="text-sm text-svt-text-secondary leading-relaxed mb-2" data-id="element-436">
                    Тариф{' '}
                    <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-svt-pink to-svt-purple" data-id="element-437">
                      {selectedPlan}
                    </span>{' '}
                    активирован
                  </p>

                  <p className="text-sm text-svt-text-muted leading-relaxed mb-8" data-id="element-438">
                    Оплата успешно проведена через{' '}
                    <span className="font-semibold text-svt-text" data-id="element-439">Tribute</span>
                    . Теперь вы можете пользоваться всеми возможностями
                    выбранного тарифа.
                  </p>

                  <button onClick={closeModal} className="w-full py-3 rounded-full bg-gradient-primary text-white font-semibold text-base hover:opacity-90 transition-all shadow-[0_0_20px_rgba(232,67,147,0.3)]" data-id="element-440">
                    Закрыть
                  </button>
                </div>}

              {modalState === 'cancel-confirm' && <div className="relative flex flex-col items-center text-center" data-id="element-441">
                  <div className="relative mb-6" data-id="element-442">
                    <div className="absolute inset-0 bg-red-500 opacity-20 blur-2xl rounded-full" data-id="element-443"></div>
                    <div className="relative w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center" data-id="element-444">
                      <XCircle size={40} className="text-red-400" strokeWidth={2} data-id="element-445" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-svt-text mb-3" data-id="element-446">
                    Отменить подписку?
                  </h3>
                  <p className="text-sm text-svt-text-secondary leading-relaxed mb-2" data-id="element-447">
                    Доступ к тарифу{' '}
                    <span className="font-semibold text-svt-text" data-id="element-448">
                      Стандарт
                    </span>{' '}
                    сохранится до{' '}
                    <span className="font-semibold text-svt-text" data-id="element-449">
                      15 марта 2026
                    </span>
                  </p>
                  <p className="text-sm text-svt-text-muted leading-relaxed mb-8" data-id="element-450">
                    После этой даты подписка не будет продлена автоматически.
                  </p>
                  <div className="flex gap-3 w-full" data-id="element-451">
                    <button onClick={closeModal} className="flex-1 py-3 rounded-full border border-svt-border text-svt-text font-semibold text-sm hover:bg-svt-surface-hover transition-all" data-id="element-452">
                      Не отменять
                    </button>
                    <button onClick={handleConfirmCancel} className="flex-1 py-3 rounded-full bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(239,68,68,0.3)]" data-id="element-453">
                      Отменить
                    </button>
                  </div>
                </div>}

              {modalState === 'cancel-success' && <div className="relative flex flex-col items-center text-center" data-id="element-454">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              duration: 0.4,
              delay: 0.1,
              type: 'spring',
              stiffness: 200
            }} className="relative mb-6" data-id="element-455">
                    <div className="absolute inset-0 bg-svt-pink opacity-20 blur-2xl rounded-full" data-id="element-456"></div>
                    <div className="relative w-20 h-20 rounded-full bg-svt-surface border-2 border-svt-border flex items-center justify-center" data-id="element-457">
                      <Check size={40} className="text-svt-pink" strokeWidth={3} data-id="element-458" />
                    </div>
                  </motion.div>
                  <h3 className="text-xl font-bold text-svt-text mb-3" data-id="element-459">
                    Подписка отменена
                  </h3>
                  <p className="text-sm text-svt-text-secondary leading-relaxed mb-2" data-id="element-460">
                    Автоматическое продление отключено
                  </p>
                  <p className="text-sm text-svt-text-muted leading-relaxed mb-8" data-id="element-461">
                    Вы можете пользоваться всеми возможностями тарифа{' '}
                    <span className="font-semibold text-svt-text" data-id="element-462">
                      Стандарт
                    </span>{' '}
                    до{' '}
                    <span className="font-semibold text-svt-text" data-id="element-463">
                      15 марта 2026
                    </span>
                    .
                  </p>
                  <button onClick={closeModal} className="w-full py-3 rounded-full bg-gradient-primary text-white font-semibold text-base hover:opacity-90 transition-all shadow-[0_0_20px_rgba(232,67,147,0.3)]" data-id="element-464">
                    Закрыть
                  </button>
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}